import type React from "react";
import type { SearchResultMessages } from "@/features/study-group-stream/utils/streamSearchResultsHelpers";
import type { SearchResultState } from "@/features/study-group-stream/utils/streamSearchResultState";

const assertUnreachable = (value: never): never => {
  throw new Error(`Unhandled search result state: ${JSON.stringify(value)}`);
};

const SEARCH_RESULT_BASE_CLASS =
  "flex-1 flex flex-col bg-background px-4 pt-2 pb-6 overflow-y-auto";
const SEARCH_RESULT_LIST_CLASS = `${SEARCH_RESULT_BASE_CLASS} gap-4`;
const SEARCH_RESULT_EMPTY_CLASS = `${SEARCH_RESULT_BASE_CLASS} items-center justify-center text-gray-500 text-center gap-3 py-10`;
const SEARCH_RESULT_STATE_CONTAINER_CLASS = `${SEARCH_RESULT_BASE_CLASS} items-center justify-center`;
const SEARCH_RESULT_REQUIRED_CLASS = "text-center text-gray-500 py-8";
const LOADING_CENTER_CLASS = `${SEARCH_RESULT_BASE_CLASS} items-center justify-center text-gray-500`;
const ERROR_CENTER_CLASS = `${SEARCH_RESULT_BASE_CLASS} items-center justify-center gap-4 text-red-500`;

export const SearchResultInvalid: React.FC<{ message: string }> = ({
  message,
}) => (
  <div className={SEARCH_RESULT_STATE_CONTAINER_CLASS}>
    <div className={SEARCH_RESULT_REQUIRED_CLASS}>{message}</div>
  </div>
);

export const SearchResultLoading: React.FC<{ message: string }> = ({
  message,
}) => <div className={LOADING_CENTER_CLASS}>{message}</div>;

export const SearchResultError: React.FC<{ message: string }> = ({
  message,
}) => <div className={ERROR_CENTER_CLASS}>{message}</div>;

export const SearchResultEmpty: React.FC<{ message: string }> = ({
  message,
}) => (
  <div className={SEARCH_RESULT_EMPTY_CLASS}>
    <div>{message}</div>
  </div>
);

export const SearchResultStateRenderer: React.FC<{
  state: SearchResultState;
  messages: SearchResultMessages;
  children: React.ReactNode;
}> = ({ state, messages, children }) => {
  switch (state) {
    case "invalid":
      return <SearchResultInvalid message={messages.invalid} />;
    case "loading":
      return <SearchResultLoading message={messages.loading} />;
    case "error":
      return <SearchResultError message={messages.error} />;
    case "empty":
      return <SearchResultEmpty message={messages.empty} />;
    case "loaded":
      return <>{children}</>;
    default:
      return assertUnreachable(state);
  }
};

export const SEARCH_RESULT_LIST_WRAPPER_CLASS = SEARCH_RESULT_LIST_CLASS;
